import { createColumnHelper } from '@tanstack/react-table';
import { filterOutStandardQueryFields } from 'common/components/table/cell-formatters/fieldHelpers';
import { PayloadFieldsCellFormatter } from 'common/components/table/cell-formatters/payloadFieldCellFormatter';
import { formatIsoDateTimeStringToDisplayDate } from 'common/utils/LEGACY_timeHelpers';
import { NavLink } from 'react-router-dom';
import { PaginationTableRowSelectionCheckbox } from 'features/pagination/paginatedTableRowSelectionCheckbox';
import { PaginationTableSelectAllRowsCheckbox } from 'features/pagination/paginatedTableSelectAllRowsCheckbox';
import { BaseSelectedRow } from 'features/row-selection/rowSelectionSlice';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';
import { GetOpportunitiesResponse } from '../types/get-opportunities-response';
import MapFieldRenderer from 'common/components/table/mapFieldRenderer';
import { createLabelsCell } from './opportunity-table-helpers';
import { convertArrayToObject } from 'common/utils/array-helpers';

const columnHelper = createColumnHelper<GetOpportunitiesResponse>();

export const opportunitiesSimpleColumnDefs = (
  componentId: string,
  currencyCode: string
) => [
  columnHelper.accessor((row) => row.id, {
    id: 'select',
    enableSorting: false,
    header: (info) => {
      const tableRows = info.table
        .getRowModel()
        .rows.map<BaseSelectedRow>((row) => ({
          rowId: row.getValue('select'),
          rowData: row.getValue('opportunityDateTime')
        }));

      return (
        <PaginationTableSelectAllRowsCheckbox
          componentId={componentId}
          tableRows={tableRows}
        />
      );
    },
    enableColumnFilter: false,
    cell: ({ row }) => (
      <PaginationTableRowSelectionCheckbox
        componentId={componentId}
        rowId={row.getValue('select')}
        rowData={row.getValue('opportunityDateTime')}
      />
    ),
    size: 50
  }),
  columnHelper.accessor((row) => row.opportunityDateTime, {
    id: 'opportunityDateTime',
    header: () => <span>Date/Time</span>,
    enableColumnFilter: false,
    cell: (info) => (
      <span className="font-bold">
        {formatIsoDateTimeStringToDisplayDate(info.getValue())}
      </span>
    )
  }),
  columnHelper.accessor((row) => row.value, {
    id: 'opportunityValue',
    header: () => <span>Value</span>,
    enableColumnFilter: false,
    cell: (info) => (
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(info.getValue())}
      </CurrencyDisplayWrapper>
    )
  }),
  columnHelper.accessor((row) => row.visitorIdentifierOptions, {
    id: 'visitorIdentifiersOptions',
    header: () => <span>Customer</span>,
    enableColumnFilter: false,
    enableSorting: false,
    size: 250,
    cell: (info) =>
      MapFieldRenderer({
        fieldsMap: info.getValue(),
        contextKey: 'visitoridentifieroptions'
      })
  }),
  columnHelper.accessor((row) => row.pipelineStage, {
    id: 'pipelineStage',
    header: () => <span>Stage</span>,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor(
    (row) => {
      return {
        header: row.firstClickSource,
        subheader:
          row.firstClickSource === 'Referral'
            ? row.firstClick?.referrerHost
            : undefined,
        fields: row.firstClickQueryStrings
      };
    },
    {
      id: 'firstClick',
      header: () => <span>First Click</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => (
        <PayloadFieldsCellFormatter
          additionalFieldsFilter={(fields) =>
            filterOutStandardQueryFields(fields)
          }
          fieldObject={info.getValue()}
          icon=""
        />
      )
    }
  ),
  columnHelper.accessor(
    (row) => {
      return {
        header: row.lastClickSource,
        subheader:
          row.lastClickSource === 'Referral'
            ? row.lastClick?.canonicalUrl
            : undefined,
        fields: row.lastClickQueryStrings
      };
    },
    {
      id: 'lastClick',
      header: () => <span>Last Click</span>,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => (
        <PayloadFieldsCellFormatter
          additionalFieldsFilter={(fields) =>
            filterOutStandardQueryFields(fields)
          }
          fieldObject={info.getValue()}
          icon=""
        />
      )
    }
  ),
  columnHelper.accessor((row) => row.labels, {
    id: 'labels',
    header: () => <span>Labels</span>,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => createLabelsCell(info.row.original)
  }),
  columnHelper.accessor((row) => row.reference, {
    id: 'reference',
    header: () => <span>Reference</span>,
    enableColumnFilter: false,
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor((row) => row.additionalDataProperties, {
    id: 'additionalDataProperties',
    header: () => <span>Additional Data</span>,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => {
      const additionalDataProperties = info.getValue() as {
        name: string;
        value: string;
      }[];

      const fieldsMap = convertArrayToObject(additionalDataProperties);

      return MapFieldRenderer({
        fieldsMap,
        contextKey: 'additionalDataProperties'
      });
    }
  }),
  columnHelper.accessor((row) => row.visitorId, {
    id: 'visitor',
    header: () => <span>Visitor</span>,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) => {
      if (!info.getValue()) {
        return null;
      } else {
        return (
          <NavLink
            title="View"
            to={`/visitors/${info.getValue()}`}
            className="transition-all bg-navy hover:bg-tertiary-600 text-white py-3 px-6 rounded text-sm"
          >
            View
          </NavLink>
        );
      }
    }
  })
];

export default opportunitiesSimpleColumnDefs;
